// front-end/src/components/Common/Feedback.js
import React, { useEffect, useState } from 'react';
import { FaBug, FaCommentAlt, FaTimes, FaCheckCircle, FaCopy, FaEnvelope, FaPaperPlane } from 'react-icons/fa';
import Toast from './Toast';
import { routeService } from '../../services/api';
import '../../css/feedback.css';

const FeedbackButton = ({ showButton = true, openSignal = 0 }) => {
    // State for modals
    const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showErrors, setShowErrors] = useState(false);

    // State for validation
    const [emailError, setEmailError] = useState('');
    const [messageError, setMessageError] = useState('');
    const [charCount, setCharCount] = useState(0);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    // State for toast notifications
    const [toasts, setToasts] = useState([]);

    useEffect(() => {
        if (openSignal > 0) {
            setShowFeedbackPopup(true);
        }
    }, [openSignal]);

    // Type display mapping
    const typeDisplay = {
        'bug': 'Bug Report',
        'feature': 'Feature Request',
        'improvement': 'Improvement Suggestion',
        'other': 'General Feedback'
    };

    // ====================
    // Toast Functions
    // ====================
    const showToast = (type, title, message, duration = 5000) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, type, title, message, duration }]);
    };

    const removeToast = (id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    // ====================
    // Validation Functions
    // ====================
    const validateEmail = (email) => {
        const trimmedEmail = email?.trim() || '';

        if (!trimmedEmail) {
            return 'Please enter your email address';
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedEmail)) {
            return 'Please enter a valid email (e.g., name@example.com)';
        }

        return '';
    };

    const validateMessage = (message) => {
        const trimmedMessage = message?.trim() || '';

        if (!trimmedMessage) {
            return 'Please share your feedback with us';
        }

        if (trimmedMessage.length < 10) {
            return 'Could you share a bit more detail? (at least 10 characters)';
        }

        if (trimmedMessage.length > 2000) {
            return 'Your message is a bit long. Please keep it under 2000 characters.';
        }

        return '';
    };

    // ====================
    // Main Submit Handler
    // ====================
    const handleFeedbackSubmit = async (e) => {
        e.preventDefault();

        // Set flag to show errors (after first submission attempt)
        setShowErrors(true);
        setHasSubmitted(true);

        // Get form data
        const formData = new FormData(e.target);
        const email = formData.get('feedbackEmail');
        const message = formData.get('feedbackMessage');

        // Validate before submitting
        const emailValidation = validateEmail(email);
        const messageValidation = validateMessage(message);

        // Set error messages
        setEmailError(emailValidation);
        setMessageError(messageValidation);

        // If there are errors, show toast and don't submit
        if (emailValidation || messageValidation) {
            showToast('warning', 'Review your details',
                'Please check the highlighted fields before submitting.');
            return;
        }

        // If no errors, proceed with submission
        setIsSubmitting(true);

        const feedbackData = {
            type: formData.get('feedbackType'),
            message: message.trim(),
            email: email.trim(),
            pageUrl: window.location.href,
            website: '',
        };

        try {
            const backendUrl = routeService.getApiBaseUrl();

            const response = await fetch(`${backendUrl}/api/feedback`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(feedbackData)
            });

            // Handle different response statuses
            if (response.status === 422) {
                const errorData = await response.json();
                showToast('error', 'Validation Error', errorData.detail || 'Please check your input and try again.');
                return;
            }

            if (response.status === 500) {
                showToast('error', 'Server Error', 'Something went wrong on our end. Please try again later.');
                return;
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                showToast('error', 'Submission Failed', errorData.message || `Error: ${response.status}`);
                return;
            }

            const result = await response.json();

            // Show success toast
            showToast('success', 'Feedback Submitted!', 'Thank you! Your feedback has been received.');

            // Reset error states
            setShowErrors(false);
            setEmailError('');
            setMessageError('');

            // Show success modal
            setSuccessData({
                id: result.data?.id,
                emailSent: result.data?.user_email_sent,
                type: feedbackData.type,
                email: feedbackData.email
            });
            setShowSuccessPopup(true);
            setShowFeedbackPopup(false);

        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Feedback error:', error);
            }

            if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
                showToast('error', 'Connection Error', 'Cannot connect to server. Please check your internet connection.');
            } else {
                showToast('error', 'Submission Failed', error.message || 'An unexpected error occurred.');
            }

        } finally {
            setIsSubmitting(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        showToast('success', 'Copied!', 'Reference ID copied to clipboard', 2000);
    };

    // ====================
    // Main Render
    // ====================
    return (
        <>
            {/* Main Feedback Button */}
            {showButton && (
                <button
                    className="whe2-feedback-btn"
                    onClick={() => setShowFeedbackPopup(true)}
                    title="Send feedback or report a bug"
                    aria-label="Open feedback form"
                >
                    <FaBug className="btn-icon" />
                    <span className="btn-text">Feedback</span>
                </button>
            )}

            {/* Toast Notifications Container */}
            <div className="toast-container">
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        type={toast.type}
                        title={toast.title}
                        message={toast.message}
                        duration={toast.duration}
                        onClose={() => removeToast(toast.id)}
                    />
                ))}
            </div>

            {/* Feedback Popup Modal */}
            {showFeedbackPopup && (
                <div className="whe2-overlay" onClick={() => {
                    setShowFeedbackPopup(false);
                    setShowErrors(false);
                    setHasSubmitted(false);
                    setEmailError('');
                    setMessageError('');
                    setCharCount(0);
                }}>
                    <div className="whe2-modal feedback-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="header-icon">
                                <FaCommentAlt />
                            </div>
                            <div className="header-content">
                                <h3>Share Your Feedback</h3>
                                <p>Help us improve Whe2 for all travelers</p>
                            </div>
                            <button
                                className="close-modal-btn"
                                onClick={() => setShowFeedbackPopup(false)}
                                aria-label="Close"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <form onSubmit={handleFeedbackSubmit} noValidate>
                            <input
                                type="text"
                                name="website"
                                autoComplete="off"
                                tabIndex={-1}
                                style={{ display: 'none' }}
                                aria-hidden="true"
                            />
                            <div className="form-group">
                                <label className="form-label">
                                    Type of Feedback
                                </label>
                                <select
                                    name="feedbackType"
                                    required
                                    className="whe2-select"
                                    defaultValue=""
                                >
                                    <option value="" disabled>Select feedback type...</option>
                                    <option value="bug">Bug Report</option>
                                    <option value="feature">Feature Request</option>
                                    <option value="improvement">Improvement Suggestion</option>
                                    <option value="other">General Feedback</option>
                                </select>
                            </div>

                            {/* Message Field - Fixed */}
                            <div className={`form-group ${showErrors && messageError ? 'has-error' : ''}`}>
                                <label className="form-label required">
                                    Your Message
                                </label>
                                <textarea
                                    name="feedbackMessage"
                                    required
                                    placeholder="Please describe your feedback in detail..."
                                    className="whe2-textarea"
                                    rows="5"
                                    onChange={(e) => {
                                        setCharCount(e.target.value.length);
                                        if (showErrors) {
                                            setMessageError(validateMessage(e.target.value));
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (showErrors) {
                                            setMessageError(validateMessage(e.target.value));
                                        }
                                    }}
                                />
                                <div className="message-hint">
                                    <span className="min-length">Share your thoughts with us</span>
                                    <span className={`char-counter ${hasSubmitted && charCount > 2000 ? 'submit-error' : ''}`}>
                                        {charCount} characters
                                    </span>
                                </div>
                                {showErrors && messageError && (
                                    <div className="error-message">
                                        <span>💡</span>
                                        <span>{messageError}</span>
                                    </div>
                                )}
                            </div>

                            {/* Email Field - Fixed */}
                            <div className={`form-group ${showErrors && emailError ? 'has-error' : ''}`}>
                                <label className="form-label required">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    name="feedbackEmail"
                                    required
                                    placeholder="your@email.com"
                                    className="whe2-input"
                                    onChange={(e) => {
                                        if (showErrors) {
                                            setEmailError(validateEmail(e.target.value));
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (showErrors) {
                                            setEmailError(validateEmail(e.target.value));
                                        }
                                    }}
                                />
                                <small className="form-hint">
                                    We'll send a confirmation to this address
                                </small>
                                {showErrors && emailError && (
                                    <div className="error-message">
                                        <span>✉️</span>
                                        <span>{emailError}</span>
                                    </div>
                                )}
                            </div>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="whe2-btn-secondary"
                                    onClick={() => setShowFeedbackPopup(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="whe2-btn-primary"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner"></span>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <FaPaperPlane />
                                            Submit Feedback
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div >
            )}

            {/* Success Popup Modal */}
            {
                showSuccessPopup && successData && (
                    <div className="whe2-overlay" onClick={() => setShowSuccessPopup(false)}>
                        <div className="whe2-modal success-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="success-icon">
                                <FaCheckCircle />
                            </div>

                            <h3>Thank You! 🎉</h3>

                            <p className="success-message">
                                Your <strong>{typeDisplay[successData.type] || successData.type}</strong> has been submitted successfully.
                            </p>

                            {/* Show email status */}
                            {successData.email && (
                                <div className="email-notification">
                                    <FaEnvelope />
                                    {successData.emailSent === true ? (
                                        <span>A confirmation email has been sent to <strong>{successData.email}</strong></span>
                                    ) : successData.emailSent === false ? (
                                        <span>Email confirmation will be sent to <strong>{successData.email}</strong></span>
                                    ) : (
                                        <span>Email confirmation is being sent to <strong>{successData.email}</strong>...</span>
                                    )}
                                </div>
                            )}

                            {successData.note && (
                                <div className="processing-note">
                                    <small>{successData.note}</small>
                                </div>
                            )}

                            {successData.id && (
                                <div className="reference-card">
                                    <div className="reference-header">
                                        <span>Reference Number</span>
                                        <button
                                            className="copy-reference-btn"
                                            onClick={() => copyToClipboard(successData.id)}
                                            title="Copy to clipboard"
                                        >
                                            <FaCopy />
                                            Copy
                                        </button>
                                    </div>
                                    <div className="reference-id">
                                        <code>{successData.id}</code>
                                    </div>
                                    <p className="reference-hint">
                                        Keep this number for your records. Quote it if you need to follow up.
                                    </p>
                                </div>
                            )}

                            <div className="next-steps">
                                <h4>What happens next?</h4>
                                <ul>
                                    <li>Our team reviews all feedback weekly</li>
                                    <li>We prioritize based on impact and feasibility</li>
                                    <li>You may hear from us if we need more details</li>
                                    <li>Check our changelog for updates on improvements</li>
                                </ul>
                            </div>

                            <div className="modal-actions">
                                <button
                                    className="whe2-btn-primary"
                                    onClick={() => setShowSuccessPopup(false)}
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </>
    );
};

export default FeedbackButton;
